/**
 * Insight / Intelligence domain types.
 *
 * Mirrors the contract of `taxhive-intelligence-service`
 * (`app/schemas/insight_schema.py`). The backend keys are snake_case on the
 * wire and `customer_id` is a numeric id server-side; in the webui we keep
 * `customerId` as a string to match the rest of the UI's customer ids
 * (`cust-001`, …) and only coerce at the HTTP boundary.
 */

export type InsightType =
  | 'tax_saving'
  | 'compliance'
  | 'deduction'
  | 'investment'
  | 'risk_alert'
  | 'optimization'
  | 'deadline'
  | 'regulatory'

export type InsightStatus =
  | 'active'
  | 'dismissed'
  | 'applied'
  | 'expired'
  | 'pending_review'

export interface Insight {
  /** Backend `insight_id`, surfaced as a string for the UI. */
  id: string
  customerId: string
  insightType: InsightType
  title: string
  description: string
  recommendations: string[]
  /** 0.0 – 1.0 */
  confidenceScore: number
  status: InsightStatus
  createdAt: string
  updatedAt: string
}

/** Filters accepted by `list` / `listForCustomer`. */
export interface InsightListFilters {
  status?: InsightStatus
  insightType?: InsightType
  customerId?: string
}

/** Payload for `generate`. */
export interface GenerateInsightsInput {
  customerId: string
  insightTypes?: InsightType[]
  forceRefresh?: boolean
}

/** Result of a `generate` call (mirrors `InsightGenerateResponse`). */
export interface GenerateInsightsResult {
  customerId: string
  insightsGenerated: number
  insightIds: string[]
  message: string
}

export const INSIGHT_TYPE_LABELS: Record<InsightType, string> = {
  tax_saving: 'Tax Saving',
  compliance: 'Compliance',
  deduction: 'Deduction',
  investment: 'Investment',
  risk_alert: 'Risk Alert',
  optimization: 'Optimization',
  deadline: 'Deadline',
  regulatory: 'Regulatory',
}

export const INSIGHT_STATUS_LABELS: Record<InsightStatus, string> = {
  active: 'Active',
  dismissed: 'Dismissed',
  applied: 'Applied',
  expired: 'Expired',
  pending_review: 'Pending Review',
}
